(() => {
  'use strict';

  const parseJSON = (node, fallback) => {
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent || '');
    } catch (error) {
      return fallback;
    }
  };

  const normalize = (value) => String(value ?? '').trim().toLowerCase();

  const initGallery = (root) => {
    const stage = root.querySelector('[data-rd-stage]');
    const track = root.querySelector('[data-rd-track]');
    const slides = Array.from(root.querySelectorAll('[data-rd-slide]'));
    const thumbs = Array.from(root.querySelectorAll('[data-rd-thumb]'));
    const prev = root.querySelector('[data-rd-gallery-prev]');
    const next = root.querySelector('[data-rd-gallery-next]');
    const current = root.querySelector('[data-rd-gallery-current]');

    if (!stage || !track || !slides.length) return null;

    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let pointerDown = false;
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let latestX = 0;
    let horizontalDrag = false;

    const clamp = (index) => Math.max(0, Math.min(index, slides.length - 1));

    const render = (index, scrollThumb = true) => {
      activeIndex = clamp(index);
      track.style.transform = `translate3d(${-activeIndex * 100}%,0,0)`;

      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === activeIndex));

      thumbs.forEach((thumb, i) => {
        const active = i === activeIndex;
        thumb.classList.toggle('is-active', active);

        if (active) {
          thumb.setAttribute('aria-current', 'true');
          if (scrollThumb) {
            thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
          }
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      if (current) current.textContent = String(activeIndex + 1);
    };

    const goToMedia = (mediaId) => {
      if (!mediaId) return;
      const index = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      if (index >= 0) render(index);
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => render(index));
    });

    prev?.addEventListener('click', () => render(activeIndex - 1));
    next?.addEventListener('click', () => render(activeIndex + 1));

    stage.addEventListener('pointerdown', (event) => {
      if (
        slides.length <= 1 ||
        event.button !== 0 ||
        event.target.closest('button,a,video,iframe,model-viewer')
      ) {
        return;
      }

      pointerDown = true;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      latestX = event.clientX;
      horizontalDrag = false;
      stage.classList.add('is-dragging');

      try {
        stage.setPointerCapture(pointerId);
      } catch (error) {}
    });

    stage.addEventListener('pointermove', (event) => {
      if (!pointerDown || event.pointerId !== pointerId) return;

      latestX = event.clientX;
      const dx = latestX - startX;
      const dy = event.clientY - startY;

      if (!horizontalDrag && Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) {
        horizontalDrag = true;
      }

      if (!horizontalDrag) return;

      event.preventDefault();

      const width = stage.clientWidth || 1;
      let dragPercent = (dx / width) * 100;

      if (
        (activeIndex === 0 && dragPercent > 0) ||
        (activeIndex === slides.length - 1 && dragPercent < 0)
      ) {
        dragPercent *= 0.18;
      }

      dragPercent = Math.max(-34, Math.min(34, dragPercent));
      track.style.transform = `translate3d(${(-activeIndex * 100) + dragPercent}%,0,0)`;
    });

    const finishPointer = (event) => {
      if (!pointerDown || (event && event.pointerId !== pointerId)) return;

      const width = stage.clientWidth || 1;
      const dx = latestX - startX;
      const threshold = Math.min(Math.max(width * 0.14, 36), 90);
      let nextIndex = activeIndex;

      if (horizontalDrag && Math.abs(dx) >= threshold) {
        nextIndex += dx < 0 ? 1 : -1;
      }

      pointerDown = false;
      stage.classList.remove('is-dragging');

      try {
        stage.releasePointerCapture(pointerId);
      } catch (error) {}

      pointerId = null;
      render(nextIndex);
    };

    stage.addEventListener('pointerup', finishPointer);
    stage.addEventListener('pointercancel', finishPointer);

    stage.addEventListener('lostpointercapture', () => {
      if (!pointerDown) return;
      pointerDown = false;
      pointerId = null;
      stage.classList.remove('is-dragging');
      render(activeIndex);
    });

    render(activeIndex, false);

    return { goToMedia };
  };

  const initProduct = (root) => {
    if (!root || root.dataset.rdProductReady === 'true') return;
    root.dataset.rdProductReady = 'true';

    const variants = parseJSON(root.querySelector('[data-rd-variants]'), []);
    const config = parseJSON(root.querySelector('[data-rd-product-config]'), {});
    const gallery = initGallery(root);

    const inputs = Array.from(root.querySelectorAll('[data-rd-option-input]'));
    const fieldsets = Array.from(root.querySelectorAll('.rd-vp__option'));

    const idInput = root.querySelector('[data-rd-variant-id-input]');
    const mainAdd = root.querySelector('[data-rd-main-add]');
    const mainAddLabel = root.querySelector('[data-rd-add-label]');
    const stickyAdd = root.querySelector('[data-rd-sticky-add]');
    const stickyLabel = root.querySelector('[data-rd-sticky-label]');
    const buyButtons = Array.from(root.querySelectorAll('[data-rd-buy-now], [data-rd-sticky-buy-now]'));
    const quantityInputs = Array.from(root.querySelectorAll('[data-rd-quantity-input]'));
    const quantityButtons = Array.from(root.querySelectorAll('[data-rd-quantity-change]'));

    const price = root.querySelector('[data-rd-price]');
    const compare = root.querySelector('[data-rd-compare]');
    const mrpLabel = root.querySelector('[data-rd-mrp-label]');
    const saving = root.querySelector('[data-rd-saving]');

    const form = root.querySelector('form[data-type="add-to-cart-form"]');
    let currentVariant = null;

    const sku = root.querySelector('[data-rd-sku]');
    const barcode = root.querySelector('[data-rd-barcode]');
    const weight = root.querySelector('[data-rd-weight]');

    const skuRow = root.querySelector('[data-rd-detail-row="sku"]');
    const barcodeRow = root.querySelector('[data-rd-detail-row="barcode"]');
    const weightRow = root.querySelector('[data-rd-detail-row="weight"]');

    const inventory = root.querySelector('[data-rd-inventory]');
    const inventoryText = root.querySelector('[data-rd-inventory-text]');

    const getSelectedOptions = () => {
      const selections = [];
      const groups = new Map();

      inputs.forEach((input) => {
        const position = Number(input.dataset.optionPosition || 0);
        if (!groups.has(position)) groups.set(position, []);
        groups.get(position).push(input);
      });

      [...groups.keys()]
        .sort((a, b) => a - b)
        .forEach((position) => {
          const checked = groups.get(position).find((input) => input.checked);
          selections[position - 1] = checked?.value || '';
        });

      return selections;
    };

    const findVariant = (selected) => {
      if (!variants.length) return null;

      if (!selected.some(Boolean)) {
        const currentId = Number(idInput?.value || 0);
        return variants.find((variant) => Number(variant.id) === currentId) || variants[0];
      }

      return (
        variants.find((variant) =>
          selected.every(
            (value, index) =>
              !value || normalize(variant.options?.[index]) === normalize(value)
          )
        ) || null
      );
    };

    const updateAvailability = (selected) => {
      inputs.forEach((input) => {
        const position = Number(input.dataset.optionPosition || 0) - 1;
        const testValue = input.value;

        const selectable = variants.some((variant) => {
          if (!variant.available) return false;

          return (variant.options || []).every((value, index) => {
            if (index === position) {
              return normalize(value) === normalize(testValue);
            }

            const chosen = selected[index];
            return !chosen || normalize(value) === normalize(chosen);
          });
        });

        input.disabled = !selectable && !input.checked;
      });
    };

    const updateOptionDetails = (selected) => {
      fieldsets.forEach((fieldset) => {
        const position = Number(fieldset.dataset.optionPosition || 0) - 1;
        const optionName = normalize(fieldset.dataset.optionName);
        const value = selected[position] || '';

        if (!value) return;

        let key = '';

        if (optionName === 'language version' || optionName === 'language') {
          key = 'language';
        } else if (optionName === 'publication' || optionName === 'publisher') {
          key = 'publisher';
        } else if (optionName === 'condition') {
          key = 'condition';
        }

        if (!key) return;

        root.querySelectorAll(`[data-rd-option-detail="${key}"]`).forEach((node) => {
          node.textContent = value;
        });

        root.querySelectorAll(`[data-rd-book-option-selected="${key}"]`).forEach((node) => {
          node.textContent = value;
        });
      });
    };

    const getQuantityRule = (variant) => {
      const min = Math.max(1, Number(variant?.quantityMin || 1));
      const increment = Math.max(1, Number(variant?.quantityIncrement || 1));
      const rawMax = Number(variant?.quantityMax || 0);
      const max = rawMax > 0 ? Math.max(min, rawMax) : Infinity;

      return { min, max, increment };
    };

    const normalizeQuantity = (value, variant = currentVariant) => {
      const { min, max, increment } = getQuantityRule(variant);
      let next = Number(value);

      if (!Number.isFinite(next)) next = min;
      next = Math.max(min, next);
      next = min + Math.round((next - min) / increment) * increment;
      next = Math.max(min, next);

      if (Number.isFinite(max)) next = Math.min(max, next);

      return next;
    };

    const syncQuantity = (value, variant = currentVariant) => {
      if (!quantityInputs.length) return;

      const rule = getQuantityRule(variant);
      const normalized = normalizeQuantity(value, variant);
      const available = Boolean(variant?.available);

      quantityInputs.forEach((input) => {
        input.min = String(rule.min);
        input.step = String(rule.increment);

        if (Number.isFinite(rule.max)) {
          input.max = String(rule.max);
        } else {
          input.removeAttribute('max');
        }

        input.value = String(normalized);
        input.disabled = !available;
      });

      quantityButtons.forEach((button) => {
        button.disabled = !available;
      });
    };

    const updateQuantityForVariant = (variant) => {
      currentVariant = variant;

      if (!quantityInputs.length) return;

      const currentValue = Number(
        root.querySelector('[data-rd-main-quantity]')?.value ||
        quantityInputs[0]?.value ||
        variant?.quantityMin ||
        1
      );

      syncQuantity(currentValue, variant);
    };

    const setPurchaseState = (variant) => {
      const exists = Boolean(variant);
      const available = Boolean(variant?.available);
      const label = !exists
        ? config.unavailableLabel
        : available
          ? config.addLabel
          : config.soldOutLabel;

      if (idInput) {
        idInput.value = variant?.id || '';
        idInput.disabled = !exists;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (mainAdd) {
        mainAdd.disabled = !available;
        mainAdd.setAttribute('aria-disabled', available ? 'false' : 'true');
      }

      if (mainAddLabel) mainAddLabel.textContent = label;

      if (stickyAdd) stickyAdd.disabled = !available;
      if (stickyLabel) stickyLabel.textContent = label;

      buyButtons.forEach((button) => {
        button.disabled = !available;
      });
    };

    const updatePrice = (variant) => {
      if (!variant) {
        price?.setAttribute('hidden', '');
        compare?.setAttribute('hidden', '');
        mrpLabel?.setAttribute('hidden', '');
        saving?.setAttribute('hidden', '');
        return;
      }

      if (price) {
        price.removeAttribute('hidden');
        price.textContent = variant.priceFormatted || '';
      }

      const hasDiscount =
        Number(variant.compareAtPrice || 0) > Number(variant.price || 0);

      if (compare) {
        compare.textContent = variant.compareFormatted || '';
        compare.toggleAttribute('hidden', !hasDiscount);
      }

      mrpLabel?.toggleAttribute('hidden', !hasDiscount);

      if (saving) {
        saving.textContent = variant.savingFormatted || '';
        saving.toggleAttribute('hidden', !hasDiscount);
      }
    };

    const updateVariantDetails = (variant) => {
      if (sku) sku.textContent = variant?.sku || '';

      const barcodeFallback = barcode?.dataset.rdBarcodeFallback || '';
      const barcodeValue = variant?.barcode || barcodeFallback;
      if (barcode) barcode.textContent = barcodeValue;

      if (weight) weight.textContent = variant?.weightFormatted || '';

      skuRow?.toggleAttribute('hidden', !variant?.sku);
      barcodeRow?.toggleAttribute('hidden', !barcodeValue);
      weightRow?.toggleAttribute('hidden', !variant?.weightFormatted);
    };

    const updateInventory = (variant) => {
      if (!inventory || !inventoryText) return;

      const threshold = Number(inventory.dataset.threshold || 0);
      const quantity = Number(variant?.inventoryQuantity || 0);
      const managed = normalize(variant?.inventoryManagement) === 'shopify';
      const shouldShow =
        Boolean(variant?.available) &&
        managed &&
        quantity > 0 &&
        threshold > 0 &&
        quantity <= threshold;

      inventory.toggleAttribute('hidden', !shouldShow);

      if (!shouldShow) {
        inventoryText.textContent = '';
        return;
      }

      const template = inventory.dataset.template || 'Only {quantity} Units Left!';
      inventoryText.textContent = template.replace('{quantity}', String(quantity));
    };

    const updateURL = (variant) => {
      if (!variant?.id || !window.history?.replaceState) return;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    const applySelection = () => {
      const selected = getSelectedOptions();
      const variant = findVariant(selected);

      updateAvailability(selected);
      updateOptionDetails(selected);
      setPurchaseState(variant);
      updatePrice(variant);
      updateVariantDetails(variant);
      updateInventory(variant);
      updateQuantityForVariant(variant);

      if (variant?.featuredMediaId) {
        gallery?.goToMedia(variant.featuredMediaId);
      }

      if (variant) updateURL(variant);
    };

    inputs.forEach((input) => {
      input.addEventListener('change', applySelection);
    });

    quantityButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled || !quantityInputs.length) return;

        const direction = Number(button.dataset.rdQuantityChange || 0);
        if (!direction) return;

        const control = button.closest('[data-rd-quantity-control]');
        const localInput = control?.querySelector('[data-rd-quantity-input]') || quantityInputs[0];
        const rule = getQuantityRule(currentVariant);
        const current = normalizeQuantity(localInput?.value, currentVariant);
        const next = current + (rule.increment * direction);

        syncQuantity(next, currentVariant);
      });
    });

    quantityInputs.forEach((input) => {
      input.addEventListener('change', () => {
        syncQuantity(input.value, currentVariant);
      });

      input.addEventListener('blur', () => {
        syncQuantity(input.value, currentVariant);
      });
    });

    stickyAdd?.addEventListener('click', () => {
      if (!mainAdd || mainAdd.disabled) return;
      mainAdd.click();
    });

    const buyNow = async (button) => {
      if (!form || button.disabled) return;

      button.disabled = true;

      try {
        const formData = new FormData(form);

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json'
          },
          body: formData
        });

        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Unable to add this book.');
        }

        window.location.assign('/checkout');
      } catch (error) {
        const wrapper = root.querySelector('.product-form__error-message-wrapper');
        const message = wrapper?.querySelector('.product-form__error-message');

        if (wrapper && message) {
          message.textContent = error.message || 'Unable to continue to checkout.';
          wrapper.hidden = false;
        }

        button.disabled = false;
      }
    };

    buyButtons.forEach((button) => {
      button.addEventListener('click', () => buyNow(button));
    });

    root.querySelector('[data-rd-share]')?.addEventListener('click', async () => {
      const shareData = {
        title: config.productTitle || document.title,
        url: window.location.href
      };

      if (navigator.share) {
        try {
          await navigator.share(shareData);
        } catch (error) {}
        return;
      }

      try {
        await navigator.clipboard.writeText(window.location.href);
      } catch (error) {}
    });

    applySelection();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-rd-varada-product]').forEach(initProduct);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
